import { Injector } from '@angular/core';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import {
  ActionButtonOutcomesType,
  APP_ZONES,
  BaseWidgetProperties,
} from '@finxone-platform/shared/sys-config-types';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { take } from 'rxjs';
import { ResetFormDataAction } from '../../../actions/form-submission.action';
import { UserProfileUpdated, UserProfileUpdateFailed } from '../../../actions/user-profile.action';
import { FormSubmissionService } from '../../../services/form-submission-service/form-submission-service.service';
import { FormActionState } from '../../../state/form-submision.state';
import { mapFormData } from '../../utils';
import { handleRedirection } from '../cta-redirection.utils';

/**
 * Submits changes to the user profile in the profile zone.
 *
 * This function retrieves necessary services and state data using the provided injector.
 * It maps the form data and checks if email and phone fields are verified.
 * If verified, it submits the profile update request and listens for the
 * `UserProfileUpdated` action. Upon success, it redirects to the profile page
 * and resets the form data. On failure, it clears the form data and handles
 * the error redirection with 'UserProfileUpdateFailed' action.
 *
 * @param injector - The Angular Injector used to retrieve services and state.
 * @param widgetProperties - The widget configuration properties for navigation.
 */
export function submitProfileZoneUserProfileChanges(
  injector: Injector,
  widgetProperties: BaseWidgetProperties,
) {
  const store = injector.get(Store);
  const actions = injector.get(Actions);
  const formSubmissionService = injector.get(FormSubmissionService);
  const formStateData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.EDIT_PROFILE_DETAILS),
  );
  const mappedFormData = mapFormData(formStateData?.formData);
  const verifiableFieldsVerified = mappedFormData['isEmailVerified'] && mappedFormData['isPhoneVerified'];
  if (!widgetProperties['zoneToNavigate']) {
    widgetProperties['zoneToNavigate'] = APP_ZONES.PROFILE;
  }
  const parsedWidgetProperties = JSON.parse(JSON.stringify(widgetProperties));
  if (verifiableFieldsVerified) {
    formSubmissionService.submitProfileUpdateRequest(mappedFormData);

    const subscribeToProfileUpdateAction = (
      action: typeof UserProfileUpdated | typeof UserProfileUpdateFailed,
      onSuccess: boolean,
    ) => {
      actions.pipe(ofActionDispatched(action), take(1)).subscribe({
        next: () => {
          if (onSuccess) {
            parsedWidgetProperties['urlToNavigate'] = 'profile';
            handleRedirection(
              parsedWidgetProperties,
              injector,
              ActionButtonOutcomesType.SUCCESS,
              true,
              false,
            );
          } else {
            handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true, false);
          }
          store.dispatch(new ResetFormDataAction());
        },
      });
    };

    subscribeToProfileUpdateAction(UserProfileUpdated, true);
    subscribeToProfileUpdateAction(UserProfileUpdateFailed, false);
  }
}
